import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, CircularProgress, Button, Paper, Alert, Snackbar } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { loadYAMLPage, YAMLPageConfig } from '../../utils/yamlLoader';
import DataTable from '../shared/DataTable';
import AIMarkingConsole from '../shared/AIMarkingConsole';
import axios from 'axios';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

interface YAMLPageProps {
  pageId: string;
}

const YAMLPage: React.FC<YAMLPageProps> = ({ pageId }) => {
  const navigate = useNavigate();
  const [config, setConfig] = useState<YAMLPageConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any[]>([]);
  const [dataLoading, setDataLoading] = useState(false);

  // State for marking submissions
  const [markingSubmissionId, setMarkingSubmissionId] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info',
  });

  // State for AI Marking Console
  const [showAIConsole, setShowAIConsole] = useState(false);
  const [aiMarkingLogs, setAiMarkingLogs] = useState<Array<{
    message: string;
    level: 'info' | 'success' | 'warning' | 'error' | 'process' | 'system';
    timestamp: string;
  }>>([]);
  const statusPollingRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const loadPage = async () => {
      setLoading(true);
      const pageConfig = await loadYAMLPage(pageId);
      setConfig(pageConfig);
      setLoading(false);

      // Load data from API if configured
      if (pageConfig?.page.api?.list) {
        await fetchData(pageConfig.page.api.list);
      }
    };
    loadPage();
  }, [pageId]);

  const fetchData = async (apiUrl: string) => {
    setDataLoading(true);
    try {
      const response = await axios.get(apiUrl);
      // Handle both {results: [], count: n} and direct array responses
      const results = response.data.results || response.data;
      setData(results);
    } catch (error) {
      console.error('Error fetching data:', error);
      setData([]);
    } finally {
      setDataLoading(false);
    }
  };

  const handleRefresh = () => {
    if (config?.page.api?.list) {
      fetchData(config.page.api.list);
    }
  };

  const handleEdit = (row: any) => {
    // Extract entity name from pageId (e.g., "schools" -> "schools")
    // Navigate to edit route with row ID
    if (row.id) {
      navigate(`/${pageId}/${row.id}/edit`);
    }
  };

  const handleDelete = async (row: any) => {
    if (!row.id || !config?.page.api?.delete) {
      setSnackbar({
        open: true,
        message: 'Delete operation not configured',
        severity: 'error',
      });
      return;
    }

    // Confirm deletion
    if (!window.confirm(`Are you sure you want to delete this ${pageId.slice(0, -1)}?`)) {
      return;
    }

    try {
      const deleteUrl = config.page.api.delete.replace('{id}', row.id);
      await axios.delete(deleteUrl);

      setSnackbar({
        open: true,
        message: 'Deleted successfully',
        severity: 'success',
      });

      // Refresh the data
      handleRefresh();
    } catch (error: any) {
      console.error('Error deleting:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Failed to delete',
        severity: 'error',
      });
    }
  };

  const handleBulkDelete = async (selectedRows: any[]) => {
    if (!config?.page.api?.delete) {
      setSnackbar({
        open: true,
        message: 'Delete operation not configured',
        severity: 'error',
      });
      return;
    }

    // Confirm bulk deletion
    const count = selectedRows.length;
    if (!window.confirm(`Are you sure you want to delete ${count} selected ${pageId}?`)) {
      return;
    }

    try {
      // Delete all selected rows
      const deletePromises = selectedRows.map(row => {
        const deleteUrl = config.page.api!.delete!.replace('{id}', row.id);
        return axios.delete(deleteUrl);
      });

      await Promise.all(deletePromises);

      setSnackbar({
        open: true,
        message: `Successfully deleted ${count} ${pageId}`,
        severity: 'success',
      });

      // Refresh the data
      handleRefresh();
    } catch (error: any) {
      console.error('Error bulk deleting:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Failed to delete some items',
        severity: 'error',
      });
    }
  };

  const handleBulkPrint = async (selectedRows: any[]) => {
    if (pageId !== 'classes') {
      setSnackbar({
        open: true,
        message: 'Print operation only available for classes',
        severity: 'error',
      });
      return;
    }

    try {
      // Fetch enrollment data for each selected class
      const classesWithEnrollments = await Promise.all(
        selectedRows.map(async (classData) => {
          const response = await axios.get(`/api/school-classes/${classData.id}/enrollments/`);
          return {
            ...classData,
            learners: response.data.learners
          };
        })
      );

      // Generate print content for all selected classes
      const now = new Date();
      const timestamp = now.toLocaleString('en-ZA', {
        hour: '2-digit',
        minute: '2-digit',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });

      const printContent = `
        <html>
          <head>
            <title>Class Lists</title>
            <style>
              @media print {
                @page { margin: 2cm; }
                .page-break { page-break-before: always; }
              }
              body {
                font-family: Arial, sans-serif;
                padding: 20px;
                max-width: 800px;
                margin: 0 auto;
              }
              .class-list {
                margin-bottom: 40px;
              }
              h1 {
                font-size: 18px;
                margin-bottom: 5px;
                font-weight: bold;
              }
              h2 {
                font-size: 16px;
                margin: 5px 0;
                font-weight: normal;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                margin: 20px 0;
              }
              th {
                text-align: left;
                padding: 8px;
                border-bottom: 1px solid #000;
                font-weight: bold;
              }
              td {
                padding: 8px;
                border-bottom: 1px solid #ddd;
              }
              .footer {
                margin-top: 20px;
                font-size: 14px;
              }
            </style>
          </head>
          <body>
            ${classesWithEnrollments.map((classData, index) => {
              const sortedLearners = [...(classData.learners || [])].sort((a, b) =>
                a.last_name.localeCompare(b.last_name)
              );

              return `
                <div class="class-list ${index > 0 ? 'page-break' : ''}">
                  <h1>School Name</h1>
                  <h2>Scratch List</h2>
                  <h2>${classData.subject} Graad ${classData.grade_level} - ${classData.name}</h2>
                  ${classData.teacher_name ? `<h2>(${classData.teacher_name})</h2>` : ''}

                  <table>
                    <thead>
                      <tr>
                        <th>Last Name</th>
                        <th>Preferred Name</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${sortedLearners.map(learner => `
                        <tr>
                          <td>${learner.last_name}</td>
                          <td>${learner.first_name}</td>
                        </tr>
                      `).join('')}
                    </tbody>
                  </table>

                  <div class="footer">
                    Total: ${sortedLearners.length} people, generated at ${timestamp}
                  </div>
                </div>
              `;
            }).join('')}
          </body>
        </html>
      `;

      // Open print window
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
        }, 250);
      }

    } catch (error: any) {
      console.error('Error loading enrollments:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to load class enrollments',
        severity: 'error',
      });
    }
  };

  const handleMarkSubmission = async (row: any) => {
    if (!row.id || !row.assessment_id) {
      setSnackbar({
        open: true,
        message: 'Missing submission or assignment information',
        severity: 'error',
      });
      return;
    }

    setMarkingSubmissionId(row.id);

    // Clear previous logs and show console
    setAiMarkingLogs([]);
    setShowAIConsole(true);

    // Helper to add console log
    const addConsoleLog = (message: string, level: 'info' | 'success' | 'warning' | 'error' | 'process' | 'system' = 'info') => {
      const timestamp = new Date().toLocaleTimeString();
      setAiMarkingLogs(prev => [...prev, { message, level, timestamp }]);
    };

    addConsoleLog('Initializing AI analysis process...', 'system');
    addConsoleLog(`Submission ID: ${row.id}`, 'info');

    try {
      // First, get the assignment to retrieve the rubric
      addConsoleLog('Fetching assignment details...', 'process');
      const assignmentResponse = await axios.get(`/api/assignments/${row.assessment_id}/`);
      const assignment = assignmentResponse.data;

      if (!assignment.rubric_id) {
        addConsoleLog('Error: Assignment does not have a rubric assigned', 'error');
        setSnackbar({
          open: true,
          message: 'Assignment does not have a rubric assigned',
          severity: 'error',
        });
        setMarkingSubmissionId(null);
        setTimeout(() => setShowAIConsole(false), 3000);
        return;
      }

      addConsoleLog(`Using Rubric ID: ${assignment.rubric_id}`, 'info');
      addConsoleLog('Sending marking request to backend...', 'process');

      // Call the AI marking endpoint
      const response = await axios.post('/api/ai-mark/', {
        submission_id: row.id,
        rubric_id: assignment.rubric_id,
      });

      if (response.data.success && !response.data.queued) {
        addConsoleLog('AI marking request accepted', 'success');

        // Start polling for status updates
        startStatusPolling(row.id);
      } else if (response.data.queued) {
        addConsoleLog(`Large file queued for marking. Position: ${response.data.queue_position}`, 'warning');
        setSnackbar({
          open: true,
          message: `Large file queued for marking. Position: ${response.data.queue_position}`,
          severity: 'info',
        });
        setMarkingSubmissionId(null);
      } else {
        addConsoleLog(`Marking failed: ${response.data.error || 'Unknown error'}`, 'error');
        setSnackbar({
          open: true,
          message: response.data.error || 'Marking failed',
          severity: 'error',
        });
        setMarkingSubmissionId(null);
        setTimeout(() => setShowAIConsole(false), 5000);
      }
    } catch (err: any) {
      console.error('Error marking submission:', err);
      addConsoleLog(`Error: ${err.response?.data?.error || err.message || 'Failed to mark submission'}`, 'error');
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to mark submission',
        severity: 'error',
      });
      setMarkingSubmissionId(null);
      setTimeout(() => setShowAIConsole(false), 5000);
    }
  };

  const startStatusPolling = (submissionId: number) => {
    // Clear any existing polling interval
    if (statusPollingRef.current) {
      clearInterval(statusPollingRef.current);
    }

    // Poll for status updates every second
    statusPollingRef.current = setInterval(async () => {
      try {
        const statusRes = await axios.get(`/api/submissions/${submissionId}/marking-status/`);
        const statusData = statusRes.data;

        // Update console with new messages
        if (statusData.messages && Array.isArray(statusData.messages)) {
          statusData.messages.forEach((msg: any) => {
            const timestamp = msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString() : new Date().toLocaleTimeString();
            const existingLog = aiMarkingLogs.find(log => log.message === msg.message && log.timestamp === timestamp);

            if (!existingLog) {
              setAiMarkingLogs(prev => [...prev, {
                message: msg.message,
                level: msg.level === 'error' ? 'error' : msg.level === 'warning' ? 'warning' : 'info',
                timestamp
              }]);
            }
          });
        }

        // Show current progress
        if (statusData.current_step) {
          const timestamp = new Date().toLocaleTimeString();
          const progressMessage = `${statusData.current_step} (${statusData.progress}%)`;

          setAiMarkingLogs(prev => {
            // Replace the last progress message or add new one
            const lastLog = prev[prev.length - 1];
            if (lastLog && lastLog.level === 'process') {
              return [...prev.slice(0, -1), { message: progressMessage, level: 'process', timestamp }];
            }
            return [...prev, { message: progressMessage, level: 'process', timestamp }];
          });
        }

        // If completed or failed, stop polling
        if (statusData.status === 'completed') {
          if (statusPollingRef.current) {
            clearInterval(statusPollingRef.current);
            statusPollingRef.current = null;
          }

          const timestamp = new Date().toLocaleTimeString();
          setAiMarkingLogs(prev => [...prev, {
            message: '✓ AI marking completed successfully!',
            level: 'success',
            timestamp
          }]);

          setSnackbar({
            open: true,
            message: 'Submission marked successfully!',
            severity: 'success',
          });

          setMarkingSubmissionId(null);
          handleRefresh(); // Refresh the table to show updated scores

          // Close console after 3 seconds
          setTimeout(() => setShowAIConsole(false), 3000);
        } else if (statusData.status === 'failed') {
          if (statusPollingRef.current) {
            clearInterval(statusPollingRef.current);
            statusPollingRef.current = null;
          }

          const timestamp = new Date().toLocaleTimeString();
          setAiMarkingLogs(prev => [...prev, {
            message: `✗ Marking failed: ${statusData.error_message || 'Unknown error'}`,
            level: 'error',
            timestamp
          }]);

          setSnackbar({
            open: true,
            message: statusData.error_message || 'Marking failed',
            severity: 'error',
          });

          setMarkingSubmissionId(null);

          // Keep console open longer for errors
          setTimeout(() => setShowAIConsole(false), 10000);
        }
      } catch (error) {
        console.error('Error polling marking status:', error);
      }
    }, 1000); // Poll every second
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (statusPollingRef.current) {
        clearInterval(statusPollingRef.current);
      }
    };
  }, []);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Build custom actions from YAML config - must be before conditional returns for hooks rules
  const tableComponent = config?.page.components?.[0];
  const customActions = React.useMemo(() => {
    const actions: any[] = [];

    // Add custom actions from YAML config
    const yamlCustomActions = tableComponent?.config?.customActions || [];
    yamlCustomActions.forEach((action: any) => {
      // Map icon names to icon components
      let icon = undefined;
      if (action.icon === 'auto_awesome') {
        icon = <AutoAwesomeIcon />;
      }

      // Determine color with proper typing
      const color: 'primary' | 'secondary' = action.variant === 'primary' ? 'primary' : 'secondary';

      actions.push({
        label: action.name,
        icon: icon,
        color: color,
        onClick: (row: any) => {
          if (action.action === 'navigate' && action.navigateTo) {
            // Replace {id} and other placeholders in navigateTo with actual row values
            const path = action.navigateTo.replace(/\{(\w+)\}/g, (match: string, key: string) => {
              return row[key] !== undefined ? row[key] : match;
            });
            navigate(path);
          }
        },
        tooltip: (row: any) => action.tooltip || action.name,
      });
    });

    return actions;
  }, [pageId, tableComponent, navigate]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!config) {
    return (
      <Box>
        <Typography variant="h5" color="error">
          Failed to load page configuration for: {pageId}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Page Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            {config.page.title}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {config.page.description}
          </Typography>
        </Box>
        <Box>
          {config.page.toolbar?.actions?.map((action: any) => (
            <Button
              key={action.id}
              variant={action.variant === 'primary' ? 'contained' : 'outlined'}
              color={action.variant === 'primary' ? 'primary' : 'inherit'}
              onClick={() => {
                if (action.route) {
                  navigate(action.route);
                } else if (action.action === 'refresh') {
                  handleRefresh();
                }
              }}
              sx={{ ml: 1 }}
            >
              {action.name}
            </Button>
          ))}
        </Box>
      </Box>

      {/* Data Table */}
      {tableComponent && (
        <Paper>
          <DataTable
            columns={tableComponent.config.columns}
            data={data}
            loading={dataLoading}
            features={tableComponent.config.features}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onBulkDelete={pageId === 'classes' ? undefined : handleBulkDelete}
            onBulkPrint={pageId === 'classes' ? handleBulkPrint : undefined}
            customActions={customActions}
          />
        </Paper>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* AI Marking Console */}
      <AIMarkingConsole
        isVisible={showAIConsole}
        logs={aiMarkingLogs}
        onClose={() => {
          setShowAIConsole(false);
          if (statusPollingRef.current) {
            clearInterval(statusPollingRef.current);
            statusPollingRef.current = null;
          }
        }}
      />
    </Box>
  );
};

export default YAMLPage;
